import { Injectable, computed, inject, signal } from '@angular/core';
import { GetImageWithGidService } from '@coin/shared/auth/data-access';
import { produce } from 'immer';
import { firstValueFrom, map, Observable } from 'rxjs';
import { CoinEmployee, Employee, EmployeesService, User } from '../../../../../api/generated';

export interface UserMenuStateModel {
  userImageSource: string;
  userDetails: Employee;
}

const STATE_DEFAULTS: UserMenuStateModel = {
  userImageSource: null,
  userDetails: undefined
};

// Auto-migrated from NGXS
@Injectable({ providedIn: 'root' })
export class UserMenuState {
  private getImageService = inject(GetImageWithGidService);
  private employeeService = inject(EmployeesService);

  private userMenuStateModel = signal<UserMenuStateModel>(STATE_DEFAULTS);

  public userImageSource = computed(() => this.userMenuStateModel().userImageSource);

  public userDetails = computed(() => this.userMenuStateModel().userDetails);

  public loadUserImage(user: User): Observable<void> {
    return this.getImageService.getImageWithStore(user.gid).pipe(map(imageSource => this.userImageLoaded(imageSource)));
  }

  private userImageLoaded(imageSource: string): void {
    this.userMenuStateModel.update(state =>
      produce(state, draft => {
        draft.userImageSource = imageSource;
      })
    );
  }

  public async loadUserDetails(user: User): Promise<void> {
    try {
      const employee = await firstValueFrom(this.employeeService.getEmployee(user.gid));
      this.userDetailsLoaded(this.mapEmployee(employee));
    } catch (/* eslint-disable-line @typescript-eslint/no-unused-vars*/ error) {
      this.userDetailsLoaded({ gid: user.gid, firstname: user.firstname, lastname: user.lastname });
    }
  }

  private userDetailsLoaded(userDetails: Employee): void {
    this.userMenuStateModel.update(state =>
      produce(state, draft => {
        draft.userDetails = userDetails;
      })
    );
  }

  private mapEmployee(coinEmployee: CoinEmployee): Employee {
    return {
      ...coinEmployee,
      firstname: coinEmployee.firstname,
      lastname: coinEmployee.lastname,
      lastnamePrefix: coinEmployee.surnamePrefix
    };
  }
}
